import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useDispatch } from "react-redux";

import { useResponsive } from "../../../../../../hooks/useResponsive";
import { useCard } from "../../../../../../reactQuery/cards";
import { useBoardName } from "../../../../../../reactQuery/boards";
import { useRemoveMultipleParams } from "../../../../../../hooks/useRemoveMultipleParams";
import { descriptionUpdate } from "../../../../../../services/apiDescription";
import { clearCheckedIds } from "../../../../../../redux/CheckboxSlice";
import { currentUserId, isAdmin } from "../../../../../../utils/constants";
import useToastPromise from "../../../../../../hooks/useToast";
import useMutate from "../../../../../../hooks/useMutate";
import { getResponseData } from "../../../../../../reactQuery/response";

export const useCardDialog = ({ cardId, setShowDetails }) => {
  const [filterDrawer, setFilterDrawer] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [cardTitle, setCardTitle] = useState("");
  const [cardDescription, setCardDescription] = useState("");
  const descRef = useRef("");
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [removeMultiParams] = useRemoveMultipleParams();
  const { showToastPromise } = useToastPromise();
  const isMobile = useResponsive("down", "md");
  const isBetween700And900 = useResponsive("between", 700, 900);

  const descriptionObj = useMemo(
    () => ({
      get: () => cardDescription,
      set: (value) => {
        setCardDescription(value);
      },
    }),
    [cardDescription]
  );

  const {
    isLoading: isCardLoading,
    card,
    isError: isCardError,
  } = useCard(cardId);
  const cardDetail = getResponseData(card, {});
  const { boardName } = useBoardName(cardDetail?.board_id);
  const board_permission = getResponseData(boardName, {})?.board_permission;

  const permissions = useMemo(() => {
    const checkIsOwnerOrAdmin =
      isAdmin() ||
      cardDetail?.board?.owner_user_id === currentUserId() ||
      board_permission?.is_admin;

    return {
      checkIsOwnerOrAdmin,
      accessForDeleteCard: checkIsOwnerOrAdmin,
      accessForEditCard:
        checkIsOwnerOrAdmin ||
        cardDetail?.reporter === currentUserId() ||
        board_permission?.can_edit_task,
      conditionCheck:
        checkIsOwnerOrAdmin ||
        (cardDetail?.assigned_to_user_id == currentUserId() &&
          board_permission?.can_update_status),
    };
  }, [board_permission, cardDetail]);

  const { mutate: descriptionUpdateMutate } = useMutate(
    descriptionUpdate,
    [],
    () => {},
    () => {
      queryClient.invalidateQueries({
        queryKey: ["card", cardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["history", cardId],
      });
    },
    () => {}
  );

  useEffect(() => {
    setCardTitle(cardDetail?.title);
  }, [cardDetail?.title]);

  const handleSave = useCallback(
    (text) => {
      const currentDescription = cardDetail?.description || "";
      const newDescription = text || "";

      if (currentDescription !== newDescription) {
        descriptionUpdateMutate({
          card_id: cardId,
          data: { description: text },
        });
      }
    },
    [cardDetail?.description, cardId, descriptionUpdateMutate]
  );

  const handleClose = useCallback(() => {
    if (isFocus) {
      handleSave();
    }
    removeMultiParams(["card_id", "brd_id"]);
    if (id) {
      if (location.pathname.includes("/feeds/")) {
        navigate(`/feeds/${id}?${searchParams.toString()}`);
      } else {
        navigate(`/board/${id}?${searchParams.toString()}`);
      }
    }
    setShowDetails(false);
    dispatch(clearCheckedIds());
  }, [
    dispatch,
    handleSave,
    id,
    isFocus,
    location.pathname,
    navigate,
    removeMultiParams,
    searchParams,
    setShowDetails,
  ]);

  const handleReload = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("card_id");
    setSearchParams(nextSearchParams);
  }, [searchParams, setSearchParams]);

  const handleFilterDrawer = useCallback((value) => {
    setFilterDrawer(value);
  }, []);

  return {
    filterDrawer,
    descriptionObj,
    isFocus,
    cardTitle,
    descRef,
    isMobile,
    isBetween700And900,
    isCardLoading,
    isCardError,
    card,
    cardDetail,
    board_permission,
    ...permissions,
    handleClose,
    handleSave,
    handleReload,
    handleFilterDrawer,
    setFilterDrawer,
    setIsFocus,
    setCardTitle,
    showToastPromise,
  };
};
